import { Box, Drawer, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import React, { useMemo } from 'react';
import { useContractPdf } from '../../../../../hooks/requests/use-contract-pdf';
import { ContractPdfViewer } from './ContractPdfViewer';
import { desanitizeFilename, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { vyPalette } from '../../../../../assets/themes/vyPalette.ts';

const DRAWER_WIDTH = '767px';

type ContractPdfDrawerProps = {
  open: boolean;
  onClose: () => void;
  contractId: string;
};

export const ContractPdfDrawer: React.FC<ContractPdfDrawerProps> = ({
  open,
  onClose,
  contractId,
}) => {
  const { data, isLoading } = useContractPdf(contractId, { enabled: open });

  const fileName = useMemo(() => {
    if (!isNotNullOrUndefined(data?.fileName)) return 'Contract Document';

    const lastDot = data.fileName.lastIndexOf('.');

    const name = lastDot !== -1 ? data.fileName.slice(0, lastDot) : data.fileName;
    const ext = lastDot !== -1 ? data.fileName.slice(lastDot) : '';

    return desanitizeFilename(name) + ext;
  }, [data?.fileName]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-vy="contract-pdf-drawer"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          height: '100vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ECEDEE',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'common.white',
          borderBottom: `1px solid ${vyPalette.divider}`,
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 2,
          paddingBottom: 2,
          gap: 2,
        }}
      >
        <Typography
          noWrap
          title={fileName}
          sx={{
            color: "text.primary",
            minWidth: 0
          }}>
          {fileName}
        </Typography>

        <IconButton onClick={onClose} size="small" aria-label="Close drawer">
          <VyIcon icon="CloseBig" width="24" height="24" viewBox="6 6 22 22" />
        </IconButton>
      </Box>
      <Stack
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {isLoading && (
          <Box sx={{ padding: 4, flex: 1 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        )}

        {!isLoading && isNotNullOrUndefined(data?.encodedPdf) && (
          <ContractPdfViewer base64EncodedPdf={data.encodedPdf} fileName={data.fileName} />
        )}

        {!isLoading && isNullOrUndefined(data?.encodedPdf) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              padding: 4,
            }}
          >
            <Typography variant="body1" sx={{
              color: "text.secondary"
            }}>
              No document available for this contract.
            </Typography>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};
